import React, { useEffect, useState } from 'react';
import { Card, Button, Modal, Form, Input, Select, InputNumber, Radio, App, Popconfirm, Tag, Spin, Empty, Collapse } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ThunderboltOutlined, DragOutlined } from '@ant-design/icons';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import MathRenderer from '../components/MathRenderer';
import AppLayout from '../components/AppLayout';
import api from '../api/client';

const DIFF_COLOUR = { foundation: '#389e0d', intermediate: '#0958d9', extension: '#c41d7f' };

function SortableStageCard({ stage, index, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: stage.stage_id });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}>
      <Card variant="outlined" style={{ borderColor: '#e8e8e8', marginBottom: 8 }} bodyStyle={{ padding: '10px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span {...attributes} {...listeners} style={{ cursor: 'grab', color: '#ccc', fontSize: 16, flexShrink: 0 }}>
            <DragOutlined />
          </span>
          <div style={{
            width: 26, height: 26, borderRadius: '50%', background: '#0a0a0a', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 700, flexShrink: 0,
          }}>
            {index + 1}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{stage.stage_name}</div>
            {stage.gate_description && (
              <div style={{ fontSize: 11, color: '#888', marginTop: 2,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {stage.gate_description}
              </div>
            )}
            <div style={{ marginTop: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
              <Tag style={{ fontSize: 10,
                color: DIFF_COLOUR[stage.difficulty],
                borderColor: DIFF_COLOUR[stage.difficulty],
                background: 'transparent' }}>
                {stage.difficulty}
              </Tag>
              <span style={{ fontSize: 11, color: '#aaa' }}>{stage.effort_minutes} min</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            <Button size="small" type="text" icon={<EditOutlined />} onClick={onEdit} />
            <Popconfirm title="Delete this stage?" onConfirm={onDelete}>
              <Button size="small" type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default function AdminTemplates() {
  const { message } = App.useApp();
  const [templates, setTemplates]       = useState([]);
  const [subjects, setSubjects]         = useState([]);
  const [stagesByTpl, setStagesByTpl]   = useState({});
  const [loading, setLoading]           = useState(true);

  const [tmplModal, setTmplModal]       = useState(false);
  const [editingTmpl, setEditingTmpl]   = useState(null);
  const [tmplForm] = Form.useForm();
  const [aiTmplModal, setAiTmplModal]   = useState(false);
  const [aiTmplForm] = Form.useForm();
  const [aiTmplGenerating, setAiTmplGenerating] = useState(false);

  const [stageModal, setStageModal]     = useState(false);
  const [stageForTpl, setStageForTpl]   = useState(null);
  const [editingStage, setEditingStage] = useState(null);
  const [stageForm] = Form.useForm();
  const [stageQuestions, setStageQuestions] = useState([]);
  const [aiGenerating, setAiGenerating]     = useState(false);
  const [aiCount, setAiCount]               = useState(3);
  const [previewIdx, setPreviewIdx]         = useState(null);
  const [activeKey, setActiveKey]           = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleStageDragEnd = (templateId) => async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const list = stagesByTpl[templateId] || [];
    const oldIdx = list.findIndex(s => s.stage_id === active.id);
    const newIdx = list.findIndex(s => s.stage_id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(list, oldIdx, newIdx);
    setStagesByTpl(prev => ({ ...prev, [templateId]: reordered }));
    try {
      await api.patch(`/api/gate/templates/${templateId}/stages/reorder`, { orderedStageIds: reordered.map(s => s.stage_id) });
    } catch {
      message.error('Failed to save order');
      loadStages(templateId);
    }
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([
        api.get('/api/admin/system-templates'),
        api.get('/api/admin/subjects'),
      ]);
      setTemplates(t.data);
      setSubjects(s.data);
    } catch { message.error('Failed to load templates'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const loadStages = async (templateId) => {
    try {
      const r = await api.get(`/api/gate/templates/${templateId}/stages`);
      setStagesByTpl(prev => ({ ...prev, [templateId]: r.data }));
    } catch {}
  };

  // ── Template CRUD ─────────────────────────────────────────────────────────
  const openCreateTmpl = () => {
    setEditingTmpl(null);
    tmplForm.resetFields();
    setTmplModal(true);
  };

  const openEditTmpl = (t) => {
    setEditingTmpl(t);
    tmplForm.setFieldsValue({ title: t.title, subject_key: t.subject_key, description: t.description });
    setTmplModal(true);
  };

  const saveTmpl = async (values) => {
    try {
      if (editingTmpl) {
        await api.patch(`/api/admin/system-templates/${editingTmpl.template_id}`, values);
        message.success('Template updated');
      } else {
        await api.post('/api/admin/system-templates', values);
        message.success('Template created');
      }
      setTmplModal(false);
      loadAll();
    } catch (e) { message.error(e.response?.data?.error || 'Save failed'); }
  };

  const deleteTmpl = async (id) => {
    try {
      await api.delete(`/api/admin/system-templates/${id}`);
      message.success('Deleted');
      loadAll();
    } catch (e) { message.error(e.response?.data?.error || 'Delete failed'); }
  };

  // ── Stage CRUD ────────────────────────────────────────────────────────────
  const openCreateStage = (templateId) => {
    setStageForTpl(templateId);
    setEditingStage(null);
    stageForm.resetFields();
    stageForm.setFieldsValue({ difficulty: 'intermediate', effort_minutes: 30 });
    setStageQuestions([]);
    setPreviewIdx(null);
    setStageModal(true);
  };

  const openEditStage = async (templateId, stage) => {
    setStageForTpl(templateId);
    setEditingStage(stage);
    stageForm.setFieldsValue(stage);
    setPreviewIdx(null);
    try {
      const r = await api.get(`/api/gate/stages/${stage.stage_id}/questions`);
      setStageQuestions(r.data.map(q => q.question_text));
    } catch { setStageQuestions([]); }
    setStageModal(true);
  };

  const handleGenerateQuestions = async () => {
    const stageName  = stageForm.getFieldValue('stage_name');
    const difficulty = stageForm.getFieldValue('difficulty') || 'intermediate';
    const tpl        = templates.find(t => t.template_id === stageForTpl);
    const subj       = subjects.find(s => s.subject_key === tpl?.subject_key);
    const subjectName = subj ? `${subj.display_name} ${subj.level}` : 'General';
    const subjectLevel = subj?.level || '';
    if (!stageName) { message.warning('Enter a stage name first'); return; }
    setAiGenerating(true);
    try {
      const res = await api.post('/api/ai/generate', {
        type: 'questions_only', subject: subjectName, level: subjectLevel, topic: stageName, difficulty, count: aiCount,
      });
      const raw    = res.data.content || '';
      const blocks = raw.split(/\*\*Question\s+\d+\*\*/i).filter(b => b.trim());
      const parsed = blocks.map(b => b.replace(/^\s*\[\d+\s*marks?\]\s*/i, '').trim()).filter(Boolean);
      setStageQuestions(prev => [...prev, ...(parsed.length ? parsed : [raw.trim()])]);
      message.success(`${parsed.length || 1} question${parsed.length !== 1 ? 's' : ''} generated`);
    } catch (e) { message.error(e.response?.data?.error || 'Generation failed'); }
    finally { setAiGenerating(false); }
  };

  const saveStage = async (values) => {
    try {
      const { stage_order, ...rest } = values;   // order is managed by drag-and-drop, not this form
      const payload = { ...rest, questions: stageQuestions.filter(q => q?.trim()) };
      if (editingStage) {
        await api.patch(`/api/gate/stages/${editingStage.stage_id}`, payload);
        message.success('Stage updated');
      } else {
        await api.post(`/api/gate/templates/${stageForTpl}/stages`, payload);
        message.success('Stage added');
        // Update stage_count locally so Collapse doesn't need a full reload.
        setTemplates(ts => ts.map(t =>
          t.template_id === stageForTpl ? { ...t, stage_count: (t.stage_count || 0) + 1 } : t
        ));
      }
      setStageModal(false);
      stageForm.resetFields();
      setStageQuestions([]);
      loadStages(stageForTpl); // refresh stage list without collapsing the panel
    } catch (e) { message.error(e.response?.data?.error || 'Save failed'); }
  };

  const handleGenerateTemplate = async (values) => {
    const subj = subjects.find(s => s.subject_key === values.subject_key);
    if (!subj) { message.warning('Select a subject first'); return; }
    setAiTmplGenerating(true);
    try {
      // 1. Generate the template structure
      const res = await api.post('/api/ai/generate', {
        type: 'template',
        subject: subj.display_name,
        level: subj.level,
        stageCount: values.stage_count || 8,
      });
      let parsed;
      try {
        const jsonMatch = res.data.content.match(/\{[\s\S]*\}/);
        parsed = jsonMatch ? JSON.parse(jsonMatch[0]) : null;
      } catch { parsed = null; }
      if (!parsed?.stages?.length) {
        message.error('AI returned an unexpected format. Try again.'); return;
      }
      // 2. Create the template
      const tmpl = await api.post('/api/admin/system-templates', {
        title: parsed.title || `${subj.display_name} Roadmap`,
        subject_key: values.subject_key,
        description: values.description || '',
      });
      // 3. Create all stages in parallel
      await Promise.all(parsed.stages.map((stage, i) =>
        api.post(`/api/gate/templates/${tmpl.data.template_id}/stages`, {
          stage_name:       stage.stage_name,
          description:      stage.description,
          gate_description: stage.gate_description,
          difficulty:       stage.difficulty || 'intermediate',
          effort_minutes:   stage.effort_minutes || 45,
          stage_order:      (i + 1) * 10,
          questions:        stage.questions || [],
        })
      ));
      message.success(`Template "${parsed.title}" created with ${parsed.stages.length} stages`);
      setAiTmplModal(false);
      aiTmplForm.resetFields();
      loadAll();
    } catch (e) { message.error(e.response?.data?.error || 'Generation failed'); }
    finally { setAiTmplGenerating(false); }
  };

  const deleteStage = async (templateId, stageId) => {
    try {
      await api.delete(`/api/gate/stages/${stageId}`);
      message.success('Stage deleted');
      loadStages(templateId);
      loadAll();
    } catch (e) { message.error(e.response?.data?.error || 'Delete failed'); }
  };

  if (loading) return <AppLayout><div style={{ textAlign: 'center', padding: 80 }}><Spin /></div></AppLayout>;

  return (
    <AppLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>MOE Templates</h1>
          <p style={{ color: '#888', marginTop: 4, marginBottom: 0 }}>
            System templates tutors can copy as a starting point.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button icon={<ThunderboltOutlined />}
            style={{ borderColor: '#fa8c16', color: '#fa8c16' }}
            onClick={() => { aiTmplForm.resetFields(); setAiTmplModal(true); }}>
            Generate with AI
          </Button>
          <Button type="primary" icon={<PlusOutlined />}
            style={{ background: '#0a0a0a', border: 'none' }}
            onClick={openCreateTmpl}>
            New template
          </Button>
        </div>
      </div>

      {templates.length === 0
        ? <Empty description="No system templates yet. Create the first one." style={{ padding: 60 }} />
        : (
          <Collapse
            accordion
            activeKey={activeKey}
        onChange={(key) => { setActiveKey(key); if (key) loadStages(Number(key)); }}
            items={templates.map(t => ({
              key: t.template_id,
              label: (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <div>
                    <span style={{ fontWeight: 600 }}>{t.title}</span>
                    <Tag style={{ marginLeft: 12 }}>{t.stage_count} stages</Tag>
                    {t.subject_display && <Tag>{t.subject_display}</Tag>}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                    <Button size="small" type="text" icon={<EditOutlined />}
                      onClick={() => openEditTmpl(t)} />
                    <Popconfirm title="Delete this template (and all its stages)?" onConfirm={() => deleteTmpl(t.template_id)}>
                      <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </div>
                </div>
              ),
              children: (
                <div>
                  <Button size="small" icon={<PlusOutlined />} onClick={() => openCreateStage(t.template_id)}
                    style={{ marginBottom: 12 }}>
                    Add stage
                  </Button>
                  {stagesByTpl[t.template_id]?.length > 0 && (
                    <div style={{ fontSize: 11, color: '#aaa', marginBottom: 8 }}>Drag to reorder stages</div>
                  )}
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleStageDragEnd(t.template_id)}>
                    <SortableContext items={(stagesByTpl[t.template_id] || []).map(s => s.stage_id)} strategy={verticalListSortingStrategy}>
                      {(stagesByTpl[t.template_id] || []).map((stage, idx) => (
                        <SortableStageCard
                          key={stage.stage_id}
                          stage={stage}
                          index={idx}
                          onEdit={() => openEditStage(t.template_id, stage)}
                          onDelete={() => deleteStage(t.template_id, stage.stage_id)}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                  {!stagesByTpl[t.template_id]?.length && (
                    <p style={{ color: '#aaa', fontSize: 13 }}>No stages yet. Click "Add stage" above.</p>
                  )}
                </div>
              ),
            }))}
          />
        )
      }

      {/* AI Generate template modal */}
      <Modal title={<span><ThunderboltOutlined style={{ color: '#fa8c16', marginRight: 8 }} />Generate full template with AI</span>}
        open={aiTmplModal} maskClosable={false}
        onCancel={() => { setAiTmplModal(false); aiTmplForm.resetFields(); }}
        footer={null} width={480}>
        <p style={{ color: '#888', fontSize: 13, marginTop: 0, marginBottom: 20 }}>
          Claude will generate a complete roadmap — title, all stages, criteria, and 3 questions per stage — based on the Singapore MOE syllabus. Takes ~30–60 seconds.
        </p>
        <Form form={aiTmplForm} layout="vertical" onFinish={handleGenerateTemplate} requiredMark={false}>
          <Form.Item label="Subject" name="subject_key" rules={[{ required: true }]}>
            <Select placeholder="Pick a subject"
              options={subjects.map(s => ({ value: s.subject_key, label: `${s.display_name} (${s.level})` }))} />
          </Form.Item>
          <Form.Item label="Number of stages" name="stage_count" initialValue={8}>
            <InputNumber min={4} max={20} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Extra instructions (optional)" name="description">
            <Input.TextArea autoSize={{ minRows: 2 }} placeholder="e.g. Focus on Paper 2 topics, skip organic chemistry" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={aiTmplGenerating}
            icon={<ThunderboltOutlined />} style={{ background: '#0a0a0a', border: 'none' }}>
            {aiTmplGenerating ? 'Generating…' : 'Generate template'}
          </Button>
        </Form>
      </Modal>

      {/* Template modal */}
      <Modal title={editingTmpl ? 'Edit template' : 'New system template'}
        open={tmplModal} maskClosable={false}
        onCancel={() => setTmplModal(false)}
        onOk={() => tmplForm.submit()}
        okButtonProps={{ style: { background: '#0a0a0a', border: 'none' } }}>
        <Form form={tmplForm} layout="vertical" onFinish={saveTmpl} requiredMark={false} style={{ marginTop: 16 }}>
          <Form.Item label="Title" name="title" rules={[{ required: true }]}>
            <Input placeholder="e.g. O-Level Pure Chemistry (5072)" />
          </Form.Item>
          <Form.Item label="Subject" name="subject_key">
            <Select allowClear placeholder="Pick a subject"
              options={subjects.map(s => ({ value: s.subject_key, label: `${s.display_name} (${s.level})` }))} />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea autoSize={{ minRows: 3 }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Stage modal */}
      <Modal title={editingStage ? 'Edit stage' : 'Add stage'}
        open={stageModal} maskClosable={false}
        onCancel={() => { setStageModal(false); setStageQuestions([]); setPreviewIdx(null); }}
        onOk={() => stageForm.submit()}
        okButtonProps={{ style: { background: '#0a0a0a', border: 'none' } }}
        width={600}>
        <Form form={stageForm} layout="vertical" onFinish={saveStage} requiredMark={false} style={{ marginTop: 16 }}>
          <Form.Item label="Stage name" name="stage_name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Acids, Bases & Salts" />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea autoSize={{ minRows: 2 }} />
          </Form.Item>
          <Form.Item label="Stage criteria" name="gate_description">
            <Input.TextArea autoSize={{ minRows: 3 }} placeholder="What must the student demonstrate?" />
          </Form.Item>

          {/* Questions with AI generation + KaTeX preview */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: '#555' }}>
                Questions <span style={{ color: '#aaa', fontWeight: 400 }}>(optional)</span>
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <InputNumber min={1} max={20} value={aiCount} onChange={v => setAiCount(v || 1)}
                  size="small" style={{ width: 60 }} />
                <Button size="small" icon={<ThunderboltOutlined style={{ color: '#fa8c16' }} />}
                  loading={aiGenerating} onClick={handleGenerateQuestions}
                  style={{ borderColor: '#fa8c16', color: '#fa8c16' }}>
                  Generate with AI
                </Button>
              </div>
            </div>
            {stageQuestions.map((q, i) => (
              <div key={i} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  {previewIdx === i
                    ? <div style={{ flex: 1, minHeight: 60, padding: '8px 12px', background: '#fafafa', border: '1px solid #e8e8e8', borderRadius: 6, fontSize: 13 }}>
                        <MathRenderer content={q} />
                      </div>
                    : <Input.TextArea autoSize={{ minRows: 2 }} value={q} placeholder={`Question ${i + 1}`}
                        onChange={e => setStageQuestions(prev => prev.map((x, xi) => xi === i ? e.target.value : x))}
                        style={{ flex: 1 }} />
                  }
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Button size="small" type="text" style={{ color: '#888', fontSize: 11 }}
                      onClick={() => setPreviewIdx(previewIdx === i ? null : i)}>
                      {previewIdx === i ? 'Edit' : 'Preview'}
                    </Button>
                    <Button type="text" danger size="small" onClick={() => setStageQuestions(prev => prev.filter((_, xi) => xi !== i))}>×</Button>
                  </div>
                </div>
              </div>
            ))}
            <Button type="dashed" icon={<PlusOutlined />} size="small"
              onClick={() => setStageQuestions(prev => [...prev, ''])}>
              Add question
            </Button>
          </div>

          <Form.Item label="Difficulty" name="difficulty">
            <Radio.Group>
              <Radio.Button value="foundation" style={{ color: DIFF_COLOUR.foundation }}>Foundation</Radio.Button>
              <Radio.Button value="intermediate" style={{ color: DIFF_COLOUR.intermediate }}>Intermediate</Radio.Button>
              <Radio.Button value="extension" style={{ color: DIFF_COLOUR.extension }}>Extension</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item label="Effort (minutes)" name="effort_minutes">
            <InputNumber min={5} max={240} step={5} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </AppLayout>
  );
}
